import React, { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '../contexts/AuthContext'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Password } from 'primereact/password'
import { Message } from 'primereact/message'
import { Link } from 'react-router-dom'
import DatabaseStatus from './DatabaseStatus'

const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormData = z.infer<typeof loginSchema>

const LoginForm: React.FC = () => {
  const { login, loading, databaseConnectionError } = useAuth()
  const [error, setError] = useState<string>('')

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: ''
    }
  })


  const onSubmit = async (data: LoginFormData) => {
    try {
      setError('')
      await login(data.username, data.password)
    } catch (err: any) {
      setError(err.message || 'Login failed')
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <i className="pi pi-spin pi-spinner text-4xl" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            MemberHub Login
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in with your LDAP credentials
          </p>
        </div>

        <DatabaseStatus className="mb-4" />

        <Card className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
            {databaseConnectionError && (
              <Message
                severity="error"
                className="w-full mb-4"
                content={
                  <div>
                    <div className="font-semibold mb-2">🔌 Database Connection Error</div>
                    <div className="text-sm">
                      The backend cannot connect to the PostgreSQL database.
                      <br />
                      Please ensure the database is running by executing:
                      <br />
                      <code className="bg-gray-100 px-1 rounded text-xs">cd database && docker-compose -f docker-compose.dev.yml up -d</code>
                    </div>
                  </div>
                }
              />
            )}
            {error && !databaseConnectionError && (
              <Message severity="error" text={error} className="w-full" />
            )}

            <div className="form-field">
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <div className="input-wrapper">
                <Controller
                  name="username"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="username"
                      value={field.value}
                      onChange={field.onChange}
                      className={`w-full ${errors.username ? 'p-invalid' : ''}`}
                      placeholder="Enter your username"
                      autoComplete="username"
                    />
                  )}
                />
              </div>
              {errors.username && (
                <small className="text-red-500 mt-1 block">{errors.username.message}</small>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="input-wrapper">
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <Password
                      id="password"
                      value={field.value}
                      onChange={field.onChange}
                      className={`w-full ${errors.password ? 'p-invalid' : ''}`}
                      inputClassName="w-full"
                      placeholder="Enter your password"
                      feedback={false}
                      toggleMask
                      autoComplete="current-password"
                    />
                  )}
                />
              </div>
              {errors.password && (
                <small className="text-red-500 mt-1 block">{errors.password.message}</small>
              )}
            </div>

            <Button
              type="submit"
              label={isSubmitting ? 'Signing in...' : 'Sign in'}
              icon={isSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-sign-in'}
              className="w-full"
              disabled={isSubmitting}
            />
          </form>
        </Card>

        <div className="text-center space-y-2">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
              Register here
            </Link>
          </p>
          <p className="text-xs text-gray-500">
            Contact your administrator if you have trouble signing in
          </p>
        </div>
      </div>
    </div>
  )
}

export default LoginForm